import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Chip, 
  Box, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  Divider,
  Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FunctionsIcon from '@mui/icons-material/Functions';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import type { GlossarioTerm } from '../../types/glossario';

interface TermoCardProps {
  termo: GlossarioTerm;
  categoriaName: string;
  expanded?: boolean;
  onClick?: () => void;
  onReferenciaClick?: (referencia: string) => void;
}

export const TermoCard: React.FC<TermoCardProps> = ({
  termo,
  categoriaName,
  expanded = false,
  onClick,
  onReferenciaClick,
}) => {
  return (
    <Card 
      sx={{ 
        mb: 2,
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? {
          boxShadow: 3,
          backgroundColor: 'action.hover',
        } : {},
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" component="h3" sx={{ fontWeight: 700 }}>
              {termo.termo}
            </Typography>
            {termo.nome_completo !== termo.termo && (
              <Typography variant="caption" color="text.secondary">
                {termo.nome_completo}
              </Typography>
            )}
          </Box>
          <Chip 
            label={categoriaName} 
            size="small" 
            color="primary" 
            variant="outlined"
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {termo.definicao_curta}
        </Typography>

        <Accordion 
          expanded={expanded} 
          onChange={onClick ? (e, _isExpanded) => {
            e.stopPropagation();
            if (onClick) onClick();
          } : undefined}
          elevation={0}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Definição Técnica
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {termo.definicao_completa}
            </Typography>

            {termo.formula && (
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  bgcolor: 'grey.100', 
                  mb: 2,
                  fontFamily: 'monospace'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FunctionsIcon fontSize="small" color="primary" />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Fórmula:
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontFamily: 'inherit' }}>
                  {termo.formula}
                </Typography>
              </Paper>
            )}

            {termo.exemplo && (
              <Box sx={{ p: 2, bgcolor: 'info.lighter', borderRadius: 1, mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'info.main' }}>
                  Exemplo Prático:
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {termo.exemplo}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {termo.tags && termo.tags.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LocalOfferIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    Tags:
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {termo.tags.map((tag, index) => (
                    <Chip 
                      key={index}
                      label={tag}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}

            {termo.referencias && termo.referencias.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Ver também:
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {termo.referencias.map((ref, index) => (
                    <Chip 
                      key={index}
                      label={ref}
                      size="small"
                      color="primary"
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation(); // Evita que o clique abra/feche o accordion
                        onReferenciaClick?.(ref);
                      }}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'primary.light',
                          borderColor: 'primary.main',
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};
